import React, { useEffect, useRef, useState } from 'react'
import {
  Typography,
  CardContent,
  CardHeader,
  Card,
  Button,
  Alert,
  CardActions,
  List,
  ListItemText,
  ListItem,
  Stepper,
  useMediaQuery,
  Step,
  StepButton,
  Container,
  AlertTitle,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon
} from '@mui/material'
import { useTheme } from '@mui/system'
import { useTranslation } from 'react-i18next'
import LoadingState from '../components/LoadingState'
import { getDevice, getPairedRuuviTag } from '../services/deviceService'
import { AssetInput, Company, Device, Project, RuuviTag, RuuviTagInput } from '../types'
import { RuuviTagType } from '../enums'
import { getProject } from '../services/projectService'
import { RuuviTagForm } from '../components/RuuviTagForm'
import { formatMacAddress } from '../utils/string'
import { createOrUpdateRuuviTag, detachRuuviTag, getRuuviTag } from '../services/ruuviTagService'
import { uploadAssetPhoto } from '../services/assetService'
import { NavLink, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { createWarningNotification } from '../redux/slices/notifications'
import PageHeader from '../components/PageHeader'
import SelectProjectStep from '../components/installation/SelectProjectStep'
import ScanQrCodeOrEnterDeviceCodeStep from '../components/installation/ScanQrCodeOrEnterDeviceCodeStep'
import RuuviTagScanner from '../components/installation/RuuviTagScanner'
import { getCompany } from '../services/companyService'
import RuuviTagDetailsList from '../components/RuuviTagDetailsList'
import Check from '@mui/icons-material/Check'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import _ from 'lodash'
import RuuviTagUninstallAlert from '../components/installation/RuuviTagUninstallAlert'
import { Add } from '@mui/icons-material'

enum Steps {
  SELECT_TAG_TYPE = 0,
  SELECT_PROJECT = 1,
  SCAN_QR_CODE_OR_ENTER_DEVICE_CODE = 2,
  ENABLE_RUUVITAG = 3,
  ADD_DETAILS = 4,
  INSTALLATION_COMPLETED = 5
}

interface SelectTagTypeStepProps {
  value: RuuviTagType | null
  onChange: (value: RuuviTagType) => void
  onNext: () => void
  onCancel: () => void
}

function SelectTagTypeStep({ value, onChange, onNext, onCancel }: SelectTagTypeStepProps) {
  const { t } = useTranslation('installation')

  const isSelectionValid = value === RuuviTagType.ATTACHED || value === RuuviTagType.DEVICE
  const selectedType = isSelectionValid ? value : null
  const isSelected = (type: RuuviTagType) => selectedType === type

  return (
    <Card>
      <CardHeader title={t('selectTagType')} />
      <CardContent sx={{ py: 0 }}>
        <List>
          <ListItemButton
            selected={isSelected(RuuviTagType.ATTACHED)}
            onClick={() => onChange(RuuviTagType.ATTACHED)}
          >
            <ListItemIcon sx={{ minWidth: 42 }}>
              {isSelected(RuuviTagType.ATTACHED) ? (
                <Check color="success" />
              ) : (
                <RadioButtonUnchecked />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('ruuviTagTypeOptionAttached')}
              secondary={t('ruuviTagTypeAttachedTooltip')}
              primaryTypographyProps={{
                sx: { order: '0!important' }
              }}
              secondaryTypographyProps={{
                sx: { order: '1!important' }
              }}
            />
          </ListItemButton>
          <ListItemButton
            selected={isSelected(RuuviTagType.DEVICE)}
            onClick={() => onChange(RuuviTagType.DEVICE)}
          >
            <ListItemIcon sx={{ minWidth: 42 }}>
              {isSelected(RuuviTagType.DEVICE) ? (
                <Check color="success" />
              ) : (
                <RadioButtonUnchecked />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t('ruuviTagTypeOptionStandalone')}
              primaryTypographyProps={{
                sx: { order: '0!important' }
              }}
            />
          </ListItemButton>
        </List>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={onNext} disabled={!isSelectionValid}>
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}
interface AddDetailsStepProps {
  project?: Project
  device?: Device
  values: RuuviTagInput
  onBack: () => void
  onExit: () => void
  onChange: (values: RuuviTagInput) => void
  onSubmit: (values: RuuviTagInput) => Promise<void>
}

function AddDetailsStep({
  project,
  device,
  values,
  onBack,
  onExit,
  onChange,
  onSubmit
}: AddDetailsStepProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation()

  /**
   * Handle the submit event.
   */
  const handleSubmit = async (values: RuuviTagInput) => {
    await onSubmit(values)
  }

  /**
   * Handle the cancel event.
   */
  const handleBack = () => {
    onBack()
  }

  return (
    <Card>
      <CardHeader
        title={t('addDetails')}
        action={
          <Button color="error" onClick={onExit}>
            {t('installation:exitInstallation')}
          </Button>
        }
      />
      <CardContent>
        <RuuviTagForm
          project={project}
          device={device}
          values={values}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          onChange={onChange}
          cancelButtonLabel={t('installation:back')}
        />
      </CardContent>
    </Card>
  )
}

interface InstallationCompletedStepProps {
  ruuviTag: RuuviTag
  onInstallNew: () => void
}

function InstallationCompletedStep({ ruuviTag, onInstallNew }: InstallationCompletedStepProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation()

  /**
   * Handle the install new action.
   */
  const handleInstallNew = () => {
    onInstallNew()
  }

  return (
    <Card>
      <CardHeader title={t('installationCompleted')} />
      <CardContent sx={{ py: 0 }}>
        <Alert color="success" sx={{ mb: 0 }}>
          <Typography>{t('tagInstalled')}</Typography>
        </Alert>

        <List>
          {ruuviTag?.asset?.name && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.name} secondary={t('name')} />
            </ListItem>
          )}

          <ListItem>
            <ListItemText primary={formatMacAddress(ruuviTag.id)} secondary={t('macAddress')} />
          </ListItem>

          <ListItem>
            <ListItemText primary={ruuviTag.deviceName} secondary={t('hostDevice')} />
          </ListItem>

          {ruuviTag?.asset?.company && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.company.name} secondary={t('company')} />
            </ListItem>
          )}

          {ruuviTag?.asset?.project && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.project.name} secondary={t('project')} />
            </ListItem>
          )}

          {ruuviTag?.asset?.level && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.level.name} secondary={t('level')} />
            </ListItem>
          )}

          {ruuviTag?.asset?.lift && (
            <ListItem>
              <ListItemText primary={ruuviTag.asset.lift.name} secondary={t('lift')} />
            </ListItem>
          )}
        </List>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        <Button variant="text" startIcon={<Add />} onClick={handleInstallNew}>
          {t('installNew')}
        </Button>

        <Button
          variant="contained"
          color="success"
          component={NavLink}
          to={`/ruuvi-tags/${ruuviTag.id}`}
        >
          {t('completed')}
        </Button>
      </CardActions>
    </Card>
  )
}

function _InstallationInstructionAlert() {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  return (
    <Alert severity="info">
      <AlertTitle sx={{ m: 0 }}>{t('pleaseFollowTheInstructionsBelow')}</AlertTitle>

      <List>
        <ListItem sx={{ py: 0 }}>
          <ListItemAvatar sx={{ minWidth: 16 }}>1.</ListItemAvatar>
          <ListItemText primary={t('makeSureThatTheTagIsTurnedOn')} />
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <ListItemAvatar sx={{ minWidth: 16 }}>2.</ListItemAvatar>
          <ListItemText primary={t('takeTheTagAsCloseAsPossibleToThePowerSupply')} />
        </ListItem>
        <ListItem sx={{ py: 0 }}>
          <ListItemAvatar sx={{ minWidth: 16 }}>3.</ListItemAvatar>
          <ListItemText primary={t('flipTheTagUpsideDownAndKeepItThereUntilItHasBeenIdentified')} />
        </ListItem>
      </List>
    </Alert>
  )
}

function clampStep(value: number): Steps {
  if (value <= Steps.SELECT_TAG_TYPE) {
    return Steps.SELECT_TAG_TYPE
  }

  if (value >= Steps.INSTALLATION_COMPLETED) {
    return Steps.INSTALLATION_COMPLETED
  }

  return value as Steps
}

function mergeRuuviTagInput(base: RuuviTagInput, overrides?: RuuviTagInput): RuuviTagInput {
  const baseAsset = ensureAssetInput(base.asset)

  const overrideAsset: Partial<AssetInput> = overrides?.asset ?? {}

  return {
    id: overrides?.id ?? base.id ?? '',
    device: overrides?.device ?? base.device ?? null,
    type: overrides?.type ?? base.type,
    asset: {
      name: overrideAsset.name ?? baseAsset.name,
      description: overrideAsset.description ?? baseAsset.description,
      project: overrideAsset.project ?? baseAsset.project,
      company: overrideAsset.company ?? baseAsset.company,
      level: overrideAsset.level ?? baseAsset.level,
      lift: overrideAsset.lift ?? baseAsset.lift,
      photo: overrideAsset.photo ?? baseAsset.photo,
      attachedAt: overrideAsset.attachedAt ?? baseAsset.attachedAt ?? null,
      detachedAt: overrideAsset.detachedAt ?? baseAsset.detachedAt ?? null
    }
  }
}

function ensureAssetInput(asset?: AssetInput): AssetInput {
  return {
    name: asset?.name ?? '',
    description: asset?.description ?? '',
    project: asset?.project ?? null,
    company: asset?.company ?? null,
    level: asset?.level ?? null,
    lift: asset?.lift ?? null,
    photo: asset?.photo ?? null,
    attachedAt: asset?.attachedAt ?? null,
    detachedAt: asset?.detachedAt ?? null
  }
}

function isRuuviTagType(value: unknown): value is RuuviTagType {
  return Object.values(RuuviTagType).includes(value as RuuviTagType)
}

export default function InstallRuuviTag() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The search params.
   */
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * The active step.
   */
  const [step, setStep] = useState<Steps>(() => {
    const s = searchParams.get('step')
    return s ? (clampStep(parseInt(s)) as Steps) : Steps.SELECT_TAG_TYPE
  })

  /**
   * The ID of the selected company.
   * Initialize from URL search params so we don't re-read them on every change
   * (avoids a update -> write -> read cycle).
   */
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(() => {
    const c = searchParams.get('company')
    return c ? parseInt(c) : null
  })

  /**
   * The selected company.
   */
  const [, setSelectedCompany] = useState<Company | null>(null)

  /**
   * The ID of the selected project.
   * Initialize from URL search params so the initial UI reflects the URL.
   */
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(() => {
    const p = searchParams.get('project')
    return p ? parseInt(p) : undefined
  })

  /**
   * The selected project.
   */
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  /**
   * The name of the selected device.
   * Initialize from URL search params so the initial UI reflects the URL.
   */
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | undefined>(() => {
    return searchParams.get('device') ?? undefined
  })

  /**
   * The selected device.
   */
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)

  /**
   * The ruuvi tag being installed.
   */
  const [ruuviTag, setRuuviTag] = useState<RuuviTag | null>(null)

  /**
   * The form input.
   */
  const [input, setInput] = useState<RuuviTagInput>(getInitialInput())

  const formSnapshotRef = useRef<string>('')

  const skipReplaceRef = useRef(false)

  // Push a history entry when a device is first selected while on the scan step.
  // This ensures the browser Back button returns from the device summary to the
  // enter-code view (both are within the same wizard step).
  const _prevDeviceNameRef = useRef<string | undefined>(undefined)

  const _initialMountRef = useRef(true)

  // Keep internal state in sync with external navigation (Back/Forward).
  // We initialize from search params on mount, but we also need to react when
  // the user navigates via the browser history which updates search params.
  useEffect(() => {
    const stepParam = searchParams.get('step')
    const stepFromUrl = stepParam ? clampStep(parseInt(stepParam)) : Steps.SELECT_TAG_TYPE

    if (stepFromUrl !== step) {
      setStep(stepFromUrl)
    }

    const typeParam = searchParams.get('type')
    const typeFromUrl = typeParam && isRuuviTagType(typeParam) ? typeParam : undefined
    if (typeFromUrl && typeFromUrl !== input.type) {
      setInput((previous) => ({
        ...previous,
        type: typeFromUrl as RuuviTagType
      }))
    }

    const projectParam = searchParams.get('project')
    const projectFromUrl = projectParam ? parseInt(projectParam) : undefined
    if (projectFromUrl !== selectedProjectId) {
      setSelectedProjectId(projectFromUrl)
    }

    const companyParam = searchParams.get('company')
    const companyFromUrl = companyParam ? parseInt(companyParam) : null
    if (companyFromUrl !== selectedCompanyId) {
      setSelectedCompanyId(companyFromUrl)
    }

    const deviceFromUrl = searchParams.get('device') ?? undefined
    if (deviceFromUrl !== selectedDeviceName) {
      setSelectedDeviceName(deviceFromUrl)
    }
  }, [searchParams])

  // Initialize state from search params and keep it in sync with the URL.

  // Sync non-step state to URL (replace current history entry so these don't create navigation entries).
  // Preserve the current `step` param so it doesn't get removed when other fields update.
  useEffect(() => {
    // If we just pushed a history entry (skipReplaceRef), don't run the replace
    // which would otherwise overwrite the pushed entry and break Back navigation.
    if (skipReplaceRef.current) {
      // reset the guard and skip this replace
      skipReplaceRef.current = false
      return
    }

    const params = new URLSearchParams()

    // Keep the step in the query so Back/Refresh restores the correct wizard step.
    params.set('step', String(step))

    if (input?.type !== undefined) {
      params.set('type', input.type)
    }

    if (selectedProjectId !== undefined) {
      params.set('project', String(selectedProjectId))
    }

    if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
      params.set('company', String(selectedCompanyId))
    }

    if (selectedDeviceName) {
      params.set('device', selectedDeviceName)
    }

    if (ruuviTag?.id) {
      params.set('ruuvitag', ruuviTag.id)
    }

    setSearchParams(params, { replace: true })
  }, [input?.type, selectedProjectId, selectedCompanyId, selectedDeviceName, ruuviTag?.id, step])

  useEffect(() => {
    // Skip during initial mount/hydration — do not push when value comes from URL.
    if (_initialMountRef.current) {
      _initialMountRef.current = false
      _prevDeviceNameRef.current = selectedDeviceName
      return
    }

    if (
      step === Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE &&
      selectedDeviceName &&
      !_prevDeviceNameRef.current
    ) {
      const params = new URLSearchParams()

      params.set('step', String(step))

      if (input?.type !== undefined) {
        params.set('type', input.type)
      }

      if (selectedProjectId !== undefined) {
        params.set('project', String(selectedProjectId))
      }

      if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
        params.set('company', String(selectedCompanyId))
      }

      params.set('device', selectedDeviceName)
      if (ruuviTag?.id) {
        params.set('ruuvitag', ruuviTag.id)
      }

      // Push a new history entry so Back navigates to the previous scan state.
      // Mark skipReplaceRef so the replace-effect does not immediately overwrite this push.
      skipReplaceRef.current = true
      setSearchParams(params)

      // reset guard on next tick as a safety fallback (in case other effects don't run)
      setTimeout(() => {
        skipReplaceRef.current = false
      }, 0)
    }

    _prevDeviceNameRef.current = selectedDeviceName
  }, [
    step,
    input?.type,
    selectedDeviceName,
    selectedProjectId,
    selectedCompanyId,
    ruuviTag?.id,
    setSearchParams
  ])

  /**
   * Update the input when the selected project changes.
   */
  useEffect(() => {
    if (selectedProjectId) {
      const controller = new AbortController()

      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            project: selectedProjectId
          }
        }
      })

      loadSelectedProject(selectedProjectId, controller)

      return () => controller.abort()
    } else {
      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            project: null,
            company: null,
            level: null,
            lift: null
          }
        }
      })

      setSelectedProject(null)
    }
  }, [selectedProjectId])

  useEffect(() => {
    if (selectedProject?.companyId) {
      const controller = new AbortController()

      setSelectedCompanyId(selectedProject.companyId)

      return () => controller.abort()
    } else {
      setSelectedCompanyId(null)
    }
  }, [selectedProject])

  useEffect(() => {
    if (selectedCompanyId) {
      const controller = new AbortController()

      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            company: selectedCompanyId
          }
        }
      })

      loadSelectedCompany(selectedCompanyId, controller)

      return () => controller.abort()
    } else {
      setInput((previous) => {
        const asset = ensureAssetInput(previous.asset)

        return {
          ...previous,
          asset: {
            ...asset,
            company: null
          }
        }
      })

      setSelectedCompany(null)
    }
  }, [selectedCompanyId])

  /**
   * Update the input when the selected device changes.
   */
  useEffect(() => {
    if (selectedDeviceName) {
      const controller = new AbortController()

      setInput((previous) => ({
        ...previous,
        device: selectedDeviceName
      }))

      loadSelectedDevice(selectedDeviceName)

      return () => controller.abort()
    } else {
      setInput((previous) => ({
        ...previous,
        device: null
      }))
      setSelectedDevice(null)
    }
  }, [selectedDeviceName])

  /**
   * Update the input when the paired ruuvi tag changes.
   */
  useEffect(() => {
    if (ruuviTag) {
      setInput((previous) => ({
        ...previous,
        id: ruuviTag.id
      }))
    } else {
      setInput((previous) => ({
        ...previous,
        id: undefined
      }))
    }
  }, [ruuviTag])

  /**
   * Handle next event.
   */
  const handleNext = () => {
    setStep((currentStep) => {
      const next = clampStep(currentStep + 1)

      return next
    })
  }

  const handleBack = () => {
    if (step <= Steps.SELECT_TAG_TYPE) {
      return
    }

    setStep((currentStep) => {
      const prev = clampStep(currentStep - 1)

      return prev
    })
  }

  const handleTagTypeChange = (type: RuuviTagType) => {
    setInput((previous) => ({
      ...previous,
      type
    }))
  }

  const handleInitialTagTypeSelect = (nextType: RuuviTagType) => {
    const previousType = input.type ?? null

    handleTagTypeChange(nextType)

    if (previousType !== nextType && ruuviTag) {
      setRuuviTag(null)
    }
  }

  const handleFormChange = (values: RuuviTagInput) => {
    const serializableValues = {
      ...values,
      asset: values.asset
        ? {
            ...values.asset,
            photo: undefined
          }
        : undefined
    }

    const serialized = JSON.stringify(serializableValues)

    if (serialized === formSnapshotRef.current) {
      return
    }

    formSnapshotRef.current = serialized

    setInput((previous) => mergeRuuviTagInput(previous, values))
  }

  /**
   * Handle the cancel event.
   */
  const handleCancel = () => {
    setStep(() => Steps.SELECT_TAG_TYPE)
    setSelectedProjectId(undefined)
    setSelectedDeviceName(undefined)
    setSelectedCompanyId(null)
    setSelectedCompany(null)
    setSelectedProject(null)
    setSelectedDevice(null)
    setRuuviTag(null)
    setInput(getInitialInput())
    formSnapshotRef.current = ''
    setSearchParams({}, { replace: true })
  }

  /**
   * Handle the submit event.
   */
  function normalizeTagId(rawId?: string | null): string | null {
    if (!rawId) {
      return null
    }
    const normalized = rawId.replace(/:/g, '').toLowerCase()
    return /^[0-9a-f]{12}$/.test(normalized) ? normalized : null
  }

  async function ensureDeviceHasNoAttachedTag(
    deviceName: string,
    desiredTagId: string
  ): Promise<void> {
    try {
      try {
        const pairedTag = await getPairedRuuviTag(deviceName)
        const pairedId = normalizeTagId(pairedTag?.id ?? null)
        if (pairedId && pairedId !== desiredTagId) {
          try {
            await detachRuuviTag({ ...pairedTag, id: pairedId })
          } catch {
            // Ignore detach failures for non-existing tags.
          }

          // Give backend a moment to clear attached state.
          const retries = 5
          for (let i = 0; i < retries; i += 1) {
            await new Promise((resolve) => setTimeout(resolve, 400))
            try {
              const refreshed = await getPairedRuuviTag(deviceName)
              const refreshedId = normalizeTagId(refreshed?.id ?? null)
              if (!refreshedId || refreshedId === desiredTagId) {
                break
              }
            } catch {
              // Treat 404 or other errors as "no attached tag".
              break
            }
          }
        }
      } catch {
        // Ignore and fall back to device data if pairing lookup fails.
      }

      const device = await getDevice(deviceName)
      let attachedId = normalizeTagId(
        device.attachedRuuviTag?.id ?? device.attachedRuuviTagId ?? null
      )

      if (!attachedId && Array.isArray(device.ruuviTags)) {
        attachedId =
          device.ruuviTags.find(
            (tag) =>
              tag.type === RuuviTagType.ATTACHED &&
              (tag.deviceName === deviceName || tag.device?.name === deviceName)
          )?.id ?? null
        attachedId = normalizeTagId(attachedId)
      }

      if (!attachedId || attachedId === desiredTagId) {
        return
      }

      let attachedTag: RuuviTag | null = device.attachedRuuviTag ?? null
      if (!attachedTag && attachedId) {
        try {
          attachedTag = await getRuuviTag(attachedId)
        } catch {
          attachedTag = null
        }
      }

      if (attachedTag) {
        await detachRuuviTag(attachedTag)
      }
    } catch {
      // Ignore; failure will surface as a conflict on save.
    }
  }

  const handleSubmit = async (values: RuuviTagInput) => {
    try {
      // FIXME: Normalization should be done when the "id" is assigned to value.
      if (values.id) {
        const normalizedId = values.id.replace(/:/g, '').toLowerCase()
        const isValidId = /^[0-9a-f]{12}$/.test(normalizedId)
        if (!isValidId) {
          dispatch(createWarningNotification(t('ruuviTagWasNotFoundByMacAddress')))
          return
        }

        values.id = normalizedId
      }

      // FIXME: Validation should be done in <RuuviTagForm>.
      if (values.type === RuuviTagType.ATTACHED) {
        if (values.device === undefined || values.device === null) {
          dispatch(createWarningNotification(t('tagInstallationFailed')))
          return
        } else if (values.id) {
          await ensureDeviceHasNoAttachedTag(values.device, values.id)
        }
      }

      // Create or update the ruuvi tag.
      const savedRuuviTag = await createOrUpdateRuuviTag(_.omit(values, 'asset.photo'))

      // Now, if photo was provided, we should upload it.
      if (savedRuuviTag.assetId !== null && values?.asset?.photo) {
        const asset = await uploadAssetPhoto(savedRuuviTag.assetId, values.asset.photo)

        // Omit nested device and ruuvi tag from asset.
        delete asset.device
        delete asset.ruuviTag

        savedRuuviTag.asset = asset
      }

      setRuuviTag(savedRuuviTag)
      setStep(Steps.INSTALLATION_COMPLETED)
    } catch (_error) {
      dispatch(createWarningNotification(t('tagInstallationFailed')))
    }
  }

  async function loadSelectedProject(id: number, controller?: AbortController) {
    setSelectedProject(await getProject(id, { signal: controller?.signal }))
  }

  async function loadSelectedCompany(id: number, controller?: AbortController) {
    setSelectedCompany(await getCompany(id, { signal: controller?.signal }))
  }

  async function loadSelectedDevice(name: string, controller?: AbortController) {
    setSelectedDevice(await getDevice(name, { signal: controller?.signal }))
  }

  function getInitialInput(): RuuviTagInput {
    const typeParam = searchParams.get('type')
    const type = typeParam && isRuuviTagType(typeParam) ? typeParam : undefined

    return {
      id: undefined,
      device: null,
      type,
      asset: {
        name: '',
        description: null,
        project: null,
        company: null,
        level: null,
        lift: null
      }
    }
  }

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Indicates if the screen is mobile.
   */
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  return (
    <React.Fragment>
      <PageHeader title={t('installRuuviTag')} />

      <Stepper
        nonLinear
        activeStep={step}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{ mt: 6, mb: 12 }}
      >
        <Step
          key={Steps.SELECT_TAG_TYPE}
          completed={step > Steps.SELECT_TAG_TYPE}
          color={step > Steps.SELECT_TAG_TYPE ? 'success' : 'primary'}
        >
          <StepButton color="inherit">{t('selectTagType')}</StepButton>
        </Step>
        <Step
          key={Steps.SELECT_PROJECT}
          completed={step > Steps.SELECT_PROJECT}
          color={step > Steps.SELECT_PROJECT ? 'success' : 'primary'}
        >
          <StepButton color="inherit">{t('selectProject')}</StepButton>
        </Step>
        <Step
          key={Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE}
          completed={step > Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE}
        >
          <StepButton color="inherit">{t('scanQrCodeOrEnterDeviceCode')}</StepButton>
        </Step>
        <Step key={Steps.ENABLE_RUUVITAG} completed={step > Steps.ENABLE_RUUVITAG}>
          <StepButton color="inherit">{t('enableRuuviTag')}</StepButton>
        </Step>
        <Step key={Steps.ADD_DETAILS} completed={step > Steps.ADD_DETAILS}>
          <StepButton color="inherit">{t('enterRuuviTagDetails')}</StepButton>
        </Step>
        <Step key={Steps.INSTALLATION_COMPLETED} completed={step === Steps.INSTALLATION_COMPLETED}>
          <StepButton color="inherit">{t('installationCompleted')}</StepButton>
        </Step>
      </Stepper>

      <Container maxWidth="md">
        {step === Steps.SELECT_TAG_TYPE ? (
          <SelectTagTypeStep
            value={input.type ?? null}
            onChange={handleInitialTagTypeSelect}
            onCancel={handleCancel}
            onNext={handleNext}
          />
        ) : step === Steps.SELECT_PROJECT ? (
          <SelectProjectStep
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            onCancel={handleCancel}
            onNext={handleNext}
          />
        ) : step === Steps.SCAN_QR_CODE_OR_ENTER_DEVICE_CODE ? (
          <ScanQrCodeOrEnterDeviceCodeStep
            initialDeviceName={selectedDeviceName}
            onChange={setSelectedDeviceName}
            onCancel={handleCancel}
            onBack={handleBack}
            withDetachAlert={false}
            withDetachHeaterAlert={false}
            onlineRequired
            onNext={handleNext}
          />
        ) : step === Steps.ENABLE_RUUVITAG ? (
          <>
            {!selectedProject || !selectedDevice ? (
              <LoadingState />
            ) : (
              <RuuviTagSetupStep
                type={input.type}
                project={selectedProject}
                device={selectedDevice}
                ruuviTag={ruuviTag}
                onChange={setRuuviTag}
                onDeviceChange={setSelectedDevice}
                onBack={handleBack}
                onExit={handleCancel}
                onNext={handleNext}
              />
            )}
          </>
        ) : step === Steps.ADD_DETAILS ? (
          <AddDetailsStep
            device={selectedDevice ?? undefined}
            project={selectedProject ?? undefined}
            values={input}
            onBack={handleBack}
            onExit={handleCancel}
            onChange={handleFormChange}
            onSubmit={handleSubmit}
          />
        ) : step === Steps.INSTALLATION_COMPLETED ? (
          <>
            {ruuviTag ? (
              <InstallationCompletedStep ruuviTag={ruuviTag} onInstallNew={handleCancel} />
            ) : (
              <Card>
                <CardHeader title={t('installationCompleted')} />
                <CardContent>
                  <Alert severity="error" variant="outlined">
                    {t('somethingWentWrong')}
                  </Alert>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Button onClick={handleCancel}>{t('cancel')}</Button>
                </CardActions>
              </Card>
            )}
          </>
        ) : null}
      </Container>
    </React.Fragment>
  )
}

interface RuuviTagSetupStepProps {
  type?: RuuviTagType
  project: Project
  device: Device
  ruuviTag: RuuviTag | null
  onChange: (ruuviTag: RuuviTag | null) => void
  onDeviceChange?: (device: Device) => void
  onNext: () => void
  onBack: () => void
  onExit: () => void
}

function RuuviTagSetupStep({
  type,
  device,
  ruuviTag,
  onChange,
  onDeviceChange,
  onNext,
  onBack,
  onExit
}: RuuviTagSetupStepProps) {
  const { t } = useTranslation('installation')

  /**
   * Indicates if the user wants to install a fixed host tag.
   */
  const wantsFixedHost =
    type === RuuviTagType.ATTACHED ||
    type === RuuviTagType.VALVE_IN ||
    type === RuuviTagType.VALVE_OUT

  /**
   * Indicates if the tag should have a fixed host device.
   */
  const isFixedHost = ruuviTag
    ? ruuviTag.type === RuuviTagType.ATTACHED ||
      ruuviTag.type === RuuviTagType.VALVE_IN ||
      ruuviTag.type === RuuviTagType.VALVE_OUT
    : false

  /**
   * Indicates if the tag is already attached to the same host device.
   */
  const isSameHostDevice = ruuviTag ? ruuviTag.deviceName === device.name : false

  /**
   * Indicates if the tag is installed.
   */
  const isInstalled = ruuviTag
    ? ruuviTag?.asset?.projectId !== undefined && ruuviTag?.asset?.projectId !== null
    : false

  function handleChange(ruuviTag: RuuviTag | null) {
    onChange(ruuviTag)
  }

  function handleBack() {
    onChange(null)
    onBack()
  }

  function handleExit() {
    onChange(null)
    onExit()
  }

  const nextIsDisabled =
    ruuviTag === null ||
    isInstalled ||
    (!wantsFixedHost && isFixedHost) ||
    (wantsFixedHost && isFixedHost && !isSameHostDevice)

  return (
    <Card>
      <CardHeader title={t('enableRuuviTag')} />
      <CardContent sx={{ pt: 0 }}>
        {ruuviTag ? (
          <>
            <RuuviTagDetailsList
              ruuviTag={ruuviTag}
              showDevice={ruuviTag.type === RuuviTagType.ATTACHED}
            />

            {ruuviTag.type === RuuviTagType.ATTACHED && ruuviTag.deviceName !== null ? (
              <Alert
                severity="warning"
                sx={{ mt: 4 }}
                action={
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    component={NavLink}
                    to={`/devices/${ruuviTag.deviceName}`}
                  >
                    {t('showDevice')}
                  </Button>
                }
              >
                {t('ruuviTagIsAttachedToDeviceWarning')}
              </Alert>
            ) : (ruuviTag.type === RuuviTagType.VALVE_IN ||
                ruuviTag.type === RuuviTagType.VALVE_OUT) &&
              ruuviTag.deviceName !== null ? (
              <Alert
                severity="warning"
                sx={{ mt: 4 }}
                action={
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    component={NavLink}
                    to={`/devices/${ruuviTag.deviceName}`}
                  >
                    {t('showDevice')}
                  </Button>
                }
              >
                {t('ruuviTagIsAttachedToValveWarning')}
              </Alert>
            ) : isInstalled ? (
              <RuuviTagUninstallAlert
                ruuviTag={ruuviTag}
                onUpdate={onChange}
                onCancel={onBack}
                sx={{ mt: 4 }}
              />
            ) : null}
          </>
        ) : (
          <RuuviTagScanner
            type={type}
            device={device}
            onChange={handleChange}
            onDeviceChange={onDeviceChange}
            onCancel={handleExit}
          />
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={handleBack}>
          {t('actions:back')}
        </Button>
        <Button variant="text" color="error" onClick={handleExit}>
          {t('exitInstallation')}
        </Button>
        <Button variant="contained" color="primary" disabled={nextIsDisabled} onClick={onNext}>
          {t('actions:next')}
        </Button>
      </CardActions>
    </Card>
  )
}
